
import store from "@/store/index"
import { initQuestion } from '@/utils/question/QuestionInitializer';
import { createAutoMsg } from '@/utils/chat/autoMsg'


/**
 * 根据 base64 字符串前几个字符判断图片类型
 * @param {string} base64Str 纯净的 base64 字符串（不包含 data:image 前缀）
 * @returns {string} 'png' | 'gif' | 'jpeg' | 'png'（默认）
 */
function getBase64ImageType(base64Str) {
    if (!base64Str) return 'png';
    // PNG: iVBORw0KGgo (对应 0x89 0x50 0x4E 0x47)
    if (base64Str.startsWith('iVBORw0KGgo')) return 'png';
    // GIF87a 或 GIF89a: R0lGODlh 或 R0lGODdh
    if (base64Str.startsWith('R0lGODlh') || base64Str.startsWith('R0lGODdh')) return 'gif';
    // JPEG: /9j/ (对应 0xFF 0xD8 0xFF)
    if (base64Str.startsWith('/9j/')) return 'jpeg';
    // 默认为 png（兼容现有逻辑）
    return 'png';
}


export function handleMathData(json, aiContent) {
    // 试题的parts
    let partsLen = aiContent.parts.length
    let part = {}
    let lastPart = aiContent.parts[partsLen - 1];
    let hasQ = false
    let hasPlotly = false
    if (lastPart) {
        hasQ = hasQuestion(lastPart)
        hasPlotly = lastPart.plotly && lastPart.plotly.length > 0;
    }
    // 当parts没有任何数据，或者parts的最后一个元素已有questions或plotly时，要新建parts
    if (partsLen === 0 || hasQ || hasPlotly) {
        part = {
            msg: "",
            questions: {},
            filename: [],
            title: "",
            recommend: [],
            plotly: [],
        }
        if (json.kgid) {
            aiContent.type = 'knowledgeGraph'
            aiContent.paintData = json.kg;
            aiContent.kgId = json.kgid;
            part.title = json.title || json.kg.name
        }
        if(json.topicmark) {
            aiContent.type = 'topicmark'
        }
        aiContent.parts.push(part)
    } else {
        // 将最后一part取出来
        part = aiContent.parts[partsLen - 1]
    }

    if (aiContent.msg.startsWith("loading", 0)) {
        aiContent.msg = ""
    }
    if (json.blockid) {
        aiContent.blockid = json.blockid
    }
    if (json.questions.length >= 1) {
        let questions = initQuestion(json.questions)
        part.questions = questions;
    }
    if (json.title) {
        part.title = json.title
    }
    if (json.filename) {
        part.filename = [...json.filename]
    }
    if (json.recommend) {
        // part.recommend = json.recommend
        aiContent.recommend = json.recommend
    }
    if (json.plotly.data) {
        part.plotly = [json.plotly]
    }

    part.msg += json.content
    // base64 按顺序添加
    if (json.base64 && json.base64.length > 0) {
        json.base64.forEach((base64Str) => {
            const imgType = getBase64ImageType(base64Str);
            part.msg += `![image](data:image/${imgType};base64,${base64Str}) `
        })
    }

    // if (json.recommend)
    //     aiContent.recommend = [...aiContent.recommend, ...json.recommend]
    // if (json.filename)
    //     aiContent.filename = [...aiContent.filename, ...json.filename]

    // ✅ 新增：将首个有标题的 part 同步到主 title
    if (!aiContent.title && aiContent.parts.length > 0) {
        const firstTitle = aiContent.parts.find(p => p.title)?.title;
        if (firstTitle) aiContent.title = firstTitle;
    }
    // 如果 json 直接提供了 title，直接覆盖（用于组卷等）
    if (json.title) {
        aiContent.title = json.title;
    }
}

function hasQuestion(parts) {
    if (!parts || !parts.questions) return
    let flag = false
    for (const type in parts.questions) {
        if (parts.questions[type].count >= 0) {
            flag = true;
            break
        }
    }
    return flag
}

export function handlePaper(json, aiContent) {
    // 从 aiContent 或其 params 中获取 sessionId / robotId
    const sessionId = aiContent.sessionId || aiContent.params?.sessionId;
    const robotId = aiContent.robotId || aiContent.params?.robotId;
    
    if (json && json.papers && json.papers.length !== 0) {
        for (let i = 0; i < json.papers.length; i++) {
            if (i === 0) {
                aiContent.isDone = 1;
                handleOnePaper(json.papers[i], aiContent);
                continue;
            }
            // 创建新消息并挂载必要字段
            const newMsg = createAutoMsg(aiContent.avatar, aiContent.delProp);
            newMsg.isDone = 1;
            newMsg.sessionId = sessionId;
            newMsg.robotId = robotId;
            handleOnePaper(json.papers[i], newMsg);
            // 通过 action 将消息加入当前会话
            store.dispatch('addMessageToConversation', { message: newMsg });
        }
    }
}

export function handleOnePaper(json, aiContent) {
    if (aiContent.msg.startsWith("loading", 0)) {
        aiContent.msg = aiContent.msg.replace("loading", "")
    }
    if (json.blockid) {
        aiContent.blockid = json.blockid
    }
    if (json.questions.length >= 1) {
        let questions = initQuestion(json.questions)
        aiContent.questions = questions
        aiContent.title = json.title
    }

    aiContent.msg += json.content

    if (json.level) {
        aiContent.level = json.level
    }
    // base64 按顺序添加
    if (json.base64 && json.base64.length > 0) {
        json.base64.forEach((base64Str) => {
            const imgType = getBase64ImageType(base64Str);
            aiContent.msg += `![image](data:image/${imgType};base64,${base64Str}) `
        })
    }

    if (json.recommend)
        aiContent.recommend = [...aiContent.recommend, ...json.recommend]
    if (json.filename)
        aiContent.filename = [...aiContent.filename, ...json.filename]
}

// 提交答案
export function handleAnswer(json, _, question) {
    let { content, answer, answer_fenxi, answer_xiangjie, comment } = json
    question.standardAnswer.content += content
    if (['MCQ', 'TF', 'SCQ'].includes(question.question_type)) {
        question.standardAnswer.answer += answer
        question.standardAnswer.answer_fenxi += answer_fenxi
        question.standardAnswer.answer_xiangjie += answer_xiangjie
        question.standardAnswer.comment += comment
    }

    // 答案解析面板
    question.isStandardEpxlCollapse = question.nanoid + "answer_expl"
    question.isAnswered = true
    let ele = document.getElementById(question.nanoid + 'answer_expl')
    // await question.nextTick(ele);
}

// 老师讲解
export function handleTeacherExplain(json, _, question) {
    let { teacher_expl } = json
    question.teacher_expl += teacher_expl

    // 是否要折叠老师讲解面板
    question.isTeacherEpxlCollapse = question.nanoid + "teacher_expl"
    let ele = document.getElementById(question.nanoid + "teacher_ref")
    // await question.nextTick(ele);
}

// 查看答案
export function handleViewAnswer(json, _, question) {
    let { content, answer, answer_fenxi, answer_xiangjie, comment } = json
    question.viewAnswer.content += content
    question.viewAnswer.answer += answer
    question.viewAnswer.answer_fenxi += answer_fenxi
    question.viewAnswer.answer_xiangjie += answer_xiangjie
    question.viewAnswer.comment += comment

    question.isView = true
    question.isViweAnswerCollapse = question.nanoid + "view_answer"
    let ele = document.getElementById(question.nanoid + "view_answer")
    // await question.nextTick(ele);
}

export function handleChatDone(aiContent) {
    aiContent.isDone = 1
    aiContent.delProp(aiContent, "controller")
    if (aiContent.msg === "loading") {
        aiContent.msg = ""
    }

}